<template>
  <div id="js-qubl"></div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, reactive, ref } from "vue"

import { useExitModal } from "@/composables/useExitModal"
import { useUserStore } from "@/stores/user"

import { GlobalFunctions } from "@/helpers/globalFunctions"

// Services
import api from "@/services/apiClient"
import GtmService, { UserFlow } from "@/services/gtmService"
import QuizService from "@/services/quizService"

// Types
import QuizPostCoachingIn from "@/shared/dtos/in/QuizPostCoachingIn"
import type { QuizData } from "@/types/quizData"
import UserPostIn from "@/shared/dtos/in/UserPostIn"
import type { ApiError } from "@/shared/apiClient"

// EMITS
const emit = defineEmits(["quizCompleted", "quizSuccess", "quizError"])

// STORES
const userStore = useUserStore()

// COMPOSABLES
const { removeExitModal } = useExitModal()

// PROPS
const props = defineProps({
  quizId: {
    type: String,
    required: true
  }
})

// EXPOSED METHODS
defineExpose({ processQuizCompletionWithNewEmail })

// DATA
const data = reactive({
  quizData: {} as QuizData
})

// FUNCTIONS (QUBL SETUP)
function listenToQublPostback(quizId: string): void {
  const qublInstance = document.querySelector("qubl-instance")

  if (!qublInstance) {
    console.error("Qubl instance not found")
    return
  }

  qublInstance.addEventListener("create", async () => {
    GtmService.quizStartEvent(UserFlow.COACHING)
  })

  qublInstance.addEventListener("postback", async (qublResponse: any) => {
    await GtmService.quizCompleteEvent(UserFlow.COACHING)

    data.quizData = qublResponse?.detail
    processQuizCompletion(data.quizData)

    // Remove quiz data in localstorage to avoid quiz retake blank screen
    localStorage.removeItem(quizId)
  })

  qublInstance.addEventListener("next", async () => {
    observeHelperText()
  })

  qublInstance.addEventListener("back", async () => {
    observeHelperText()
  })
}

const observer = ref<MutationObserver | null>(null)

const observeHelperText = () => {
  const qublInstance = document.querySelector("qubl-instance")?.shadowRoot
  const qublQuiz = qublInstance?.querySelector("qubl-quiz")?.shadowRoot

  if (qublQuiz && !observer.value) {
    observer.value = new MutationObserver((mutationsList) => {
      for (let mutation of mutationsList) {
        if (mutation.type === "childList") {
          const questionContainer = qublQuiz?.querySelector(".question-container")
          const helperText = qublQuiz.querySelector("#helper-text:not(#helper-text.moved)")

          if (helperText) {
            if (questionContainer) {
              helperText.classList.add("moved")
              questionContainer.appendChild(helperText)
            }
          }
        }
      }
    })

    observer.value.observe(qublQuiz, { childList: true, subtree: true })
  }
}

const unobserveHelperText = () => {
  if (observer.value) {
    observer.value.disconnect()
  }
}

// FUNCTIONS (QUIZ COMPLETION)
async function processQuizCompletion(quizData: QuizData): Promise<void> {
  emit("quizCompleted")

  removeExitModal()

  try {
    const { quizUid, instanceUid, answers } = quizData

    // Store user in database
    let userSource = GlobalFunctions.getUserSourceData() || {}
    userSource["quiz_page"] = window.location.pathname

    const newUser: UserPostIn = {
      fullName: answers.fullName,
      email: answers.email,
      phoneNumber: answers.phoneNumber,
      gender: answers.gender,
      zipCode: answers.zipCode || null,
      userSource,
      smsConsent: !!answers.phoneNumber // if they provide phone in this quiz, they consented to SMS
    }

    try {
      await api.user.post(newUser)
      await userStore.loadUserData()
    } catch (error) {
      console.error("Error storing user in database", error)
      throw error
    }

    // Store quiz answers in database
    const processedAnswers = QuizService.removeUserVariablesFromAnswers(JSON.parse(JSON.stringify(answers)))
    const coachingQuizAnswers = processedAnswers as QuizPostCoachingIn

    coachingQuizAnswers.qublInstanceUuid = instanceUid
    coachingQuizAnswers.qublQuizUuid = quizUid
    coachingQuizAnswers.industrySelection = QuizService.getIndustryValues(answers.industrySelection)

    try {
      await api.quiz.postCoaching(coachingQuizAnswers)
      await GtmService.signUpEvent(UserFlow.COACHING)
    } catch (error) {
      console.error("Error storing coaching quiz answers in database", error)
      throw error
    }

    emit("quizSuccess")
  } catch (error) {
    emit("quizError", error as ApiError, quizData)
  }
}

function processQuizCompletionWithNewEmail(quizData: QuizData): void {
  data.quizData = quizData
  processQuizCompletion(data.quizData)
}

// Lifecycle
onMounted(() => {
  GlobalFunctions.storeUserSourceData()
  QuizService.loadQublInstance(props.quizId, "")
  listenToQublPostback(props.quizId)
})

onUnmounted(() => {
  unobserveHelperText()
})
</script>
